import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { generateClassName, ObjTest } from "jzfz-web-utils";
import './style.less';
import CustomPopover from "../customPopover/CustomPopover";
import CustomPortal from '../customPortal/CustomPortal';
import AutoSizer from "../autoSizer/AutoSizer";
import { hightlightText } from '../../util/highlight';


// 弹窗位置方向枚举
enum PopoverDirectionEnum {
    TOP = 'TOP',    // 文字上方
    BOTTOM = 'BOTTOM',  // 文字下方
}


interface offset {
    x: number;
    y: number;
}


export interface WordExplanationProps {
    className?: string;
    style?: CSSProperties;
    wordClassName?: string; // 名词元素class
    mouseEnterDelay?: number;   // 鼠标移入弹窗显示延迟（毫秒）
    mouseLeaveDelay?: number;   // 鼠标移出弹窗隐藏延迟(毫秒)
    title?: string;
    content: string;    // 文本内容
    popoverWidth?: number;  // 弹窗宽度
    popoverClassName?: string;  // 弹窗额外样式
    offset?: offset; // 水平、垂直偏移量
    contentRender?: (content: string) => React.ReactNode;	// 自定义渲染内容
    titleName?: string; // 名词元素专用属性名
    dataSource?: string[];  // 名词集合
    tipContent?: React.ReactNode;   // 弹窗内容
    onMouseOver?: (word: string) => void;   // 鼠标移入名词元素上时触发
}


/**
 * 名词解释组件
 * @param props
 * @constructor
 */
const WordExplanation = (props: WordExplanationProps) => {
    const {
        className = '',
        style = {},
        wordClassName = 'custom-word-explanation',
        mouseEnterDelay = 500,
        mouseLeaveDelay = 100,
        title = '',
        content = '',
        popoverWidth = 400,
        popoverClassName = '',
        offset = { x: 0, y: 5 },
        contentRender,
        titleName = 'data-explanation-word',
        dataSource = [],
        tipContent = null,
        onMouseOver = () => { },
    } = props;

    const [visible, setVisible] = useState<boolean>(false);  // 提示窗口显隐
    const [position, setPosition] = useState<any>({ top: 0, left: 0 }); // 弹窗定位
    const [direction, setDirection] = useState<PopoverDirectionEnum>(PopoverDirectionEnum.BOTTOM); // 弹窗位置(文字上方/下发)
    const [htmlContent, setHtmlContent] = useState<string>('');
    const [popoverHeight, setPopoverHeight] = useState<number | string>('auto');    // 弹窗高度
    const [hasAppend, setHasAppend] = useState<boolean>(false); // 弹窗是否已经挂载到容器中
    const debounceHandler = useRef<any>(null);  // 鼠标移入名词元素时触发的事件句柄
    const mouseEnterDebounce = useRef<any>(null);    // 鼠标移入显示弹窗防抖
    const mouseLeaveDebounce = useRef<any>(null);    // 鼠标移出显示弹窗防抖
    const positionRef = useRef<any>({});    // 弹窗位置缓存
    const elementHeight = useRef<number>(0);  // 名词元素高度
    const popoverRef = useRef<any>(null);   // 弹窗组件引用
    const containerRef = useRef<any>(null); // 容器引用


    useEffect(() => {
        init();
        return () => {
            clearAllTimeout();
        }
    }, [content, dataSource])


    /**
     * 清除所有防抖定时器
     */
    const clearAllTimeout = () => {
        if (mouseEnterDebounce.current) {
            clearTimeout(mouseEnterDebounce.current);
        }
        if (mouseLeaveDebounce.current) {
            clearTimeout(mouseLeaveDebounce.current);
        }
    }


    /**
     * 创建术语包裹节点
     * @param text
     * @param keywords
     */
    const createWrapNode = (text: string, keywords: string): Node => {
        let wrapNode = document.createElement('span');
        wrapNode.classList.add(wordClassName);
        wrapNode.setAttribute(titleName, keywords);
        wrapNode.innerText = text;
        return wrapNode;
    }


    /**
     * 初始化
     */
    const init = () => {
        if (!ObjTest.isStr(content) || !content || !content.trim()) {
            setHtmlContent('');
            return;
        }
        let body = new DOMParser().parseFromString(content, 'text/html').body;
        // 术语过滤、排序
        let termList = dataSource.filter((v: string) => body.innerText.indexOf(v) > -1).sort((p, n) => n.length - p.length);
        const options = {
            matchLonger: true,
            replaceOnce: true,
            getWrapNode: createWrapNode,
        }
        let text = hightlightText(content, termList, options);
        setHtmlContent(text);
    }


    /**
     * 检查节点是否是名词节点或是名词节点的子节点
     * @param target
     */
    const checkNode = (target: HTMLElement): HTMLElement => {
        if (!target || !containerRef.current) {
            return null;
        }
        if (target.classList.contains(wordClassName) && target.hasAttribute(titleName)) {
            return target;
        }

        let nodes = containerRef.current.querySelectorAll(`.${wordClassName}`);
        for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].contains(target)) {
                return nodes[i];
            }
        }

        return null;
    }


    debounceHandler.current = (target: HTMLElement) => {
        let word = target.getAttribute(titleName);
        if (!word) {
            return;
        }

        // 显示弹窗
        const { left, top, height, width } = target.getBoundingClientRect();
        const halfWidth = Math.round(width / 2);
        const pos = { top: top + height + offset.y, left: left + halfWidth - Math.round(popoverWidth / 2) - offset.x };
        // 校正定位，保证悬浮工具在视口内
        const { width: bodyWidth } = document.body.getBoundingClientRect();
        // 超出右侧
        if (pos.left + Number(popoverWidth) > bodyWidth) {
            pos.left = bodyWidth - popoverWidth;
        }
        // 超出左侧
        if (pos.left < 0) {
            pos.left = 0;
        }

        setPosition(pos);
        setPopoverHeight('auto');
        setDirection(PopoverDirectionEnum.BOTTOM);
        positionRef.current = pos;
        elementHeight.current = height;
        setVisible(true);
        setHasAppend(true);
        onMouseOver(word);
    }


    /**
     * 鼠标移入名词元素
     * @param e
     */
    const mouseOverHandle = (e: any) => {
        e.persist();    // 防止react 16 及更早版本crash
        const { target } = e;

        let node = checkNode(target);
        if (!node) {
            return;
        }

        clearAllTimeout();
        mouseEnterDebounce.current = setTimeout(() => debounceHandler.current(node), mouseEnterDelay);
    }


    /**
     * 鼠标移出名词元素
     * @param e
     */
    const mouseOutHandle = (e: any) => {
        e.persist();
        const { target } = e;

        let node = checkNode(target);
        if (!node) {
            return;
        }

        clearAllTimeout();
        mouseLeaveDebounce.current = setTimeout(() => {
            setVisible(false);
        }, mouseLeaveDelay)
    }


    /**
     * 弹窗尺寸变动时触发
     * @param rect
     */
    const resize = (rect: any) => {
        const { height } = rect;
        if (!positionRef || height === 0) {
            return;
        }
        const { top, left } = positionRef.current;
        const { height: bodyHeight } = document.body.getBoundingClientRect();

        let totalHeight = top + offset.y + height;
        let diff = totalHeight - bodyHeight;
        let direction = PopoverDirectionEnum.BOTTOM;

        if (totalHeight > bodyHeight) {
            let popoverTop = top + offset.y;
            let popoverHeight = height;

            //  弹窗底部超过下边界的占比超过1/3
            if (diff / height > 0.3) {
                popoverTop = top - elementHeight.current - offset.y - height;
                popoverHeight = height;
                // 如果弹窗top超过上边界
                if (popoverTop <= 0) {
                    popoverTop = 0;
                    popoverHeight = top - offset.y - elementHeight.current;
                }
                direction = PopoverDirectionEnum.TOP;
            }else {
                popoverHeight = height - diff;
            }

            setDirection(direction);
            setPosition({ left, top: popoverTop});
            setPopoverHeight(popoverHeight);
        }
    }


    const htmlProps = (): any => {
        if (ObjTest.isFunc(contentRender)) {
            return {};
        }
        return { dangerouslySetInnerHTML: { __html: htmlContent } };
    }


    /**
     * 弹窗显隐变化
     * @param visible
     */
    const visibleChange = (visible: boolean) => {
        clearAllTimeout();
        setVisible(visible);
    }


    return (
        <>
            <span
                ref={containerRef}
                className={[className].join(' ')}
                style={{ ...style }}
                title={title}
                onMouseOver={mouseOverHandle}
                onMouseOut={mouseOutHandle}
                {...htmlProps()}
            >
                {ObjTest.isFunc(contentRender) ? contentRender(htmlContent) : null}
            </span>
            {
                hasAppend && (
                    <CustomPortal>
                        <CustomPopover
                            ref={popoverRef}
                            className={generateClassName([
                                'word-explanation-popover-wrap',
                                direction === PopoverDirectionEnum.TOP ? 'popover-top' : '',
                                popoverClassName,
                            ])}
                            visible={visible}
                            position={position}
                            onVisibleChange={visibleChange}
                            width={popoverWidth}
                            height={popoverHeight}
                            content={
                                <AutoSizer className="word-explanation-content" onResize={resize}>
                                    <div className="word-explanation-inner-content">{tipContent}</div>
                                </AutoSizer>
                            }
                        />
                    </CustomPortal>
                )
            }
        </>
    )
}


export default WordExplanation;
